import React from 'react';

import { Alert, Box, Paper, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import type { PivotColumn, PivotRow } from './PivotTable';
import { formatValue } from './cards';

type Props = {
  title: string;
  subtitle?: string;
  lines: ReadonlyArray<PivotColumn>;
  days: ReadonlyArray<PivotRow>;
  problem?: string | null;
};

const SALE_BAR = '#8fd3c1';
const REFUND_BAR = '#2b8a9e';
const BAR_TRACK = 560;
const isRefund = (key: string) => key.includes('refund');

export default function DailyLinesView({ title, subtitle, lines, days, problem }: Props) {
  const largest = Math.max(1, ...days.flatMap((day) => lines.map((line) => Math.abs(day.values[line.key] ?? 0))));
  return (
    <Paper data-lens-pdf-card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {problem ? (
        <Alert severity="info">{problem}</Alert>
      ) : (
        <Table size="small" sx={{ '& td': { py: 0.25, borderBottom: 'none' } }}>
          <TableBody>
            {days.map((day) =>
              lines.map((line, index) => {
                const value = day.values[line.key] ?? null;
                return (
                  <TableRow
                    key={`${day.label}-${line.key}`}
                    sx={
                      index === lines.length - 1
                        ? { '& td': { borderBottom: 1, borderColor: 'divider', pb: 1 } }
                        : undefined
                    }
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', color: 'text.secondary' }}>
                      {index === 0 ? day.label : ''}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.label}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            py: '7px',
                            pl: `${Math.max(value ? 2 : 0, Math.round((Math.abs(value ?? 0) / largest) * BAR_TRACK))}px`,
                            bgcolor: isRefund(line.key) ? REFUND_BAR : SALE_BAR,
                          }}
                        />
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {formatValue(value, line.format)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }),
            )}
            {!days.length && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No rows for these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
